// add spell check
// Move "print all notes" into Previous notes

import React, {useState, useEffect, useRef, useMemo} from 'react';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Autosave,
  BalloonToolbar,
  Bold,
  Essentials,
  FontColor,
  Heading,
  Highlight,
  Italic,
  List,
  ListProperties,
  Paragraph,
  Underline,
  WordCount,
  EditorConfig,
} from 'ckeditor5';

// eslint-disable-next-line import/no-unresolved
import 'ckeditor5/ckeditor5.css';
import TimeAgo from 'timeago-react';
import {Box, Button, Grid, Typography, useTheme} from '@mui/material';
import {isDate} from 'lodash';
import {Add} from '@mui/icons-material';
import {GetHandoverNotes} from '../../schema-types/GetHandoverNotes';
import {themeColors} from '../../theme/Theme';
import {useHandoverNoteStyles} from '../../features/notes/handoverNoteStyles';
import {ReviewPrintableNotes} from '../../features/notes/PrintableNotes';
import {
  FormikNote,
  HandoverNoteFields,
  getExtraText,
  getNoteCharLimit,
  getReviewedValueFromField,
  isOverNoteCharLimit,
  useOnUpdateNoteMutation,
} from './HandoverNotesHelpers';
import {useAutoField} from '../../components/AutoForm/useAutoForm';
import {PreviousNotesV2} from '../../features/notes/PreviousNotesV2';
import {UPDATES} from '../../shared/UPDATES';

const LICENSE_KEY = 'GPL';

export const useIsAtrReadOnly = (
  noteType: HandoverNoteFields,
  handoverData: GetHandoverNotes
) => {
  const isAtrReadOnly = useMemo(() => {
    if (
      noteType === 'Attitude to risk' &&
      handoverData.reviewDetails?.requireRiskProfile
    ) {
      return true;
    }
    return false;
  }, [noteType, handoverData.reviewDetails?.requireRiskProfile]);

  return isAtrReadOnly;
};

export const NotesEditorHandoverV2: React.FC<{
  currentNotesType: HandoverNoteFields;
  readOnly: boolean;
  _onUpdateNote: ReturnType<typeof useOnUpdateNoteMutation>['_onUpdateNote'];
  handoverData: GetHandoverNotes;
}> = ({currentNotesType, readOnly: _readOnly, _onUpdateNote, handoverData}) => {
  const isAtrReadOnly = useIsAtrReadOnly(currentNotesType, handoverData);
  const readOnly = _readOnly || isAtrReadOnly;

  const theme = useTheme();
  const classes = useHandoverNoteStyles();
  const bodyFieldName = `${currentNotesType}.body`;
  const markupFieldName = `${currentNotesType}.markupBody`;
  const [parentField, parentMeta, parentHelpers] =
    useAutoField<FormikNote>(currentNotesType);
  const [bodyField, bodyMeta, bodyHelpers] =
    useAutoField<FormikNote['body']>(bodyFieldName);
  const [markupField, markupMeta, markupHelpers] =
    useAutoField<FormikNote['markupBody']>(markupFieldName);
  const [characterCount, setCharacterCount] = useState(0);
  const editorContainerRef = useRef(null);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const editorWordCountRef = useRef<HTMLDivElement | null>(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [savingState, setSaving] = useState<'saving' | Date | 'not edited'>('not edited');
  useEffect(() => {
    setIsLayoutReady(true);

    return () => setIsLayoutReady(false);
  }, []);

  const config: {editorConfig: EditorConfig} | null = useMemo(() => {
    if (!isLayoutReady) {
      return null;
    }

    const placeholder = parentField.value.reviewed
      ? `NO CHANGES. No note required.`
      : `Enter ${currentNotesType} notes here...${getExtraText(currentNotesType)}`;

    const editorConfig: EditorConfig = {
      toolbar: {
        items: [
          'heading',
          '|',
          'fontSize',
          'fontFamily',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'bold',
          'italic',
          'underline',
          '|',
          'link',
          'highlight',
          '|',
          'bulletedList',
          'numberedList',
          'todoList',
        ],
        shouldNotGroupWhenFull: false,
      },
      plugins: [
        Autosave,
        BalloonToolbar,
        Bold,
        Essentials,
        FontColor,
        Heading,
        Highlight,
        Italic,
        List,
        ListProperties,
        Paragraph,
        Underline,
        WordCount,
      ],
      balloonToolbar: [
        'bold',
        'italic',
        '|',
        'link',
        '|',
        'bulletedList',
        'numberedList',
      ],
      fontFamily: {
        supportAllValues: true,
      },
      fontSize: {
        options: [10, 12, 14, 'default', 18, 20, 22],
        supportAllValues: true,
      },
      heading: {
        options: [
          {
            model: 'paragraph',
            title: 'Paragraph',
            class: 'ck-heading_paragraph',
          },
          {
            model: 'heading1',
            view: 'h1',
            title: 'Heading 1',
            class: 'ck-heading_heading1',
          },
          {
            model: 'heading2',
            view: 'h2',
            title: 'Heading 2',
            class: 'ck-heading_heading2',
          },
          {
            model: 'heading3',
            view: 'h3',
            title: 'Heading 3',
            class: 'ck-heading_heading3',
          },
          {
            model: 'heading4',
            view: 'h4',
            title: 'Heading 4',
            class: 'ck-heading_heading4',
          },
          {
            model: 'heading5',
            view: 'h5',
            title: 'Heading 5',
            class: 'ck-heading_heading5',
          },
          {
            model: 'heading6',
            view: 'h6',
            title: 'Heading 6',
            class: 'ck-heading_heading6',
          },
        ],
      },
      licenseKey: LICENSE_KEY,
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
          toggleDownloadable: {
            mode: 'manual',
            label: 'Downloadable',
            attributes: {
              download: 'file',
            },
          },
        },
      },
      list: {
        properties: {
          styles: true,
          startIndex: true,
          reversed: true,
        },
      },
      placeholder,
      wordCount: {
        displayWords: false,
        onUpdate: ({characters}) => {
          setCharacterCount(characters);
        },
      },
      autosave: {
        waitingTime: 1000, // in ms
        save(editor) {
          // Read the count from the plugin, not React state: this closure is
          // built inside useMemo and would only ever see a stale count.
          const {characters} = editor.plugins.get('WordCount');
          if (isOverNoteCharLimit(characters, currentNotesType)) {
            // Persisting over-limit text would exceed the Curo field cap and
            // the backend now rejects it; the banner explains why (ON-9417).
            return Promise.resolve();
          }
          const newMarkupBody = editor.getData();
          markupHelpers.setValue(UPDATES._4101, newMarkupBody);
          return _onUpdateNote({
            variables: {
              noteId: parentField.value.id,
              name: parentField.value.name,
              markupBody: newMarkupBody,
              reviewed: getReviewedValueFromField(parentField.value),
            },
          });
        },
      },
    };
    return {editorConfig};
  }, [isLayoutReady, currentNotesType, parentField.value.reviewed]);

  // Maybe needed?
  // useEffect(() => {
  //   // if the editor has been saved for 5 seconds, hide display
  //   const timeout = setTimeout(() => {
  //     if (savingState === 'saved') {
  //       setSaving('not edited');
  //     }
  //   }, 5000);
  //   return () => {
  //     clearTimeout(timeout);
  //   };
  // }, [savingState]);

  // Update the "Status: Saving..." information.
  function displayStatus(editor: ClassicEditor) {
    const pendingActions = editor.plugins.get('PendingActions');

    pendingActions.on('change:hasAny', (evt, propertyName, newValue) => {
      if (newValue) {
        setSaving('saving');
      } else {
        setSaving(new Date());
      }
    });
  }

  const charLimit = getNoteCharLimit(currentNotesType);
  const overLimit = isOverNoteCharLimit(characterCount, currentNotesType);

  return (
    <Box borderColor={themeColors.greyLight} pt={2} pb={2}>
      <Box display="flex" justifyContent="space-between">
        <div />
        <Box>
          <ReviewPrintableNotes handoverData={handoverData} />
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography sx={classes.noteTitle}>
            Current {currentNotesType.toLowerCase()} notes:
          </Typography>
          {!readOnly && parentField.value.reviewed ? (
            <Box>
              <Typography color={theme.palette.primary.main} sx={{mb: 2}}>
                <strong>Currently no changes</strong>
              </Typography>
              <Button
                sx={{mb: 2}}
                variant="contained"
                onClick={() =>
                  parentHelpers.setValue(UPDATES._4100, {
                    ...parentField.value,
                    reviewed: false,
                  })
                }
                startIcon={<Add color="info" />}
              >
                Add {currentNotesType.toLocaleLowerCase()} note
              </Button>
            </Box>
          ) : (
            <Box
              mt={1}
              className="main-container"
              sx={{
                background: overLimit
                  ? `${themeColors.lightAlert} !important`
                  : undefined,
              }}
            >
              {isAtrReadOnly && (
                <Box bgcolor={themeColors.lightAlert} p={2} mb={2}>
                  <Typography variant="h6">
                    Note: The <strong>Attitude to Risk</strong> notes section has now been
                    replaced by the new <strong>Risk</strong> tab.
                  </Typography>
                  <Typography variant="body2">
                    Any historic entries previously recorded in this section are still
                    accessible at the bottom of the Risk tab, should you need to refer to
                    them while completing the risk profiling exercise.
                  </Typography>
                </Box>
              )}
              <div
                className={`editor-container editor-container_classic-editor editor-container_include-word-count ${
                  overLimit ? 'over-limit' : ''
                }`}
                ref={editorContainerRef}
              >
                <div className="editor-container__editor">
                  <div ref={editorRef}>
                    {config?.editorConfig && (
                      <CKEditor
                        onReady={(editor: ClassicEditor) => {
                          const wordCount = editor.plugins.get('WordCount');
                          editorWordCountRef.current?.appendChild(
                            wordCount.wordCountContainer
                          );
                          // if (editor?.ui?.view?.editable?.element) {
                          //   editor.ui.view.editable.element.style.minHeight = '300px';
                          // }
                          displayStatus(editor);
                        }}
                        onAfterDestroy={() => {
                          Array.from(editorWordCountRef?.current?.children || []).forEach(
                            (child) => child.remove()
                          );
                        }}
                        disabled={readOnly || parentField.value.reviewed}
                        data={parentField.value.markupBody || ''}
                        editor={ClassicEditor}
                        config={config?.editorConfig}
                      />
                    )}
                  </div>
                </div>
                <Box
                  bgcolor={overLimit ? themeColors.lightAlert : undefined}
                  display="flex"
                  p={2}
                  justifyContent="space-between"
                >
                  {overLimit ? (
                    <Box>Cannot save, over character limit</Box>
                  ) : (
                    <>
                      {savingState === 'saving' ? (
                        <Box>Saving...</Box>
                      ) : isDate(savingState) ? (
                        <Box color={themeColors.darkGreen}>
                          <b>
                            Last saved:{' '}
                            <TimeAgo opts={{minInterval: 30}} datetime={savingState} />
                          </b>
                        </Box>
                      ) : (
                        <Box />
                      )}
                    </>
                  )}
                  <Box color={overLimit ? themeColors.red : themeColors.black}>
                    Chars: {characterCount}/{charLimit}
                  </Box>
                </Box>
              </div>
            </Box>
          )}
        </Grid>
        <Grid item xs={6}>
          <PreviousNotesV2
            page={currentNotesType}
            allPreviousNotes={handoverData?.previousNotes || []}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
